import { Bold, Heading2, Info, List, ListOrdered } from 'lucide-react';
import { applyMarkupAction, type MarkupAction } from './markupActions';

const BUTTONS: Array<{ action: MarkupAction; label: string; icon: typeof Bold }> = [
  { action: 'bold', label: 'Bold', icon: Bold },
  { action: 'heading', label: 'Subheading', icon: Heading2 },
  { action: 'bullets', label: 'Bullet list', icon: List },
  { action: 'numbered', label: 'Numbered list', icon: ListOrdered },
  { action: 'callout', label: 'Callout box', icon: Info },
];

/**
 * Formatting buttons for the slide-body textarea: they rewrite the value
 * via the pure markup transforms and restore the selection, so authors
 * never have to remember the syntax (the markup stays visible and
 * hand-editable for those who prefer typing it).
 */
export function MarkupToolbar({
  textareaRef,
  value,
  onChange,
}: {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
}) {
  function apply(action: MarkupAction) {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const result = applyMarkupAction(value, start, end, action);
    // Write the new value into the DOM first and place the selection, so
    // when React commits the matching controlled value it finds the DOM
    // already in sync and leaves the caret alone (a value swap during
    // commit resets the caret to the end).
    if (el) {
      el.value = result.value;
      el.focus();
      el.setSelectionRange(result.start, result.end);
    }
    onChange(result.value);
  }

  return (
    <div className="flex flex-wrap gap-1" role="toolbar" aria-label="Formatting">
      {BUTTONS.map(({ action, label, icon: Icon }) => (
        <button
          key={action}
          type="button"
          title={label}
          aria-label={label}
          onClick={() => apply(action)}
          className="rounded-token-sm border border-line/10 bg-surface-2 p-1.5 text-fg-muted hover:bg-surface-inset hover:text-fg"
        >
          <Icon className="h-4 w-4" />
        </button>
      ))}
    </div>
  );
}
